import { usePassportProvider } from "@/context";
import { useGetWalletGems, useGetWalletLatestEarnings } from "@/hooks/api";
import { useAccessToken } from "@/hooks/useAccessToken";
import { authorizedPost } from "@/utils/request";
import { trackLegacyEvent } from "@analytics";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BONUS_CODES } from "./config";
import type { BonusDetails, BonusError, GenericBonusModalState } from "./types";

const findBonusCode = (
  code: string | string[] | undefined,
): BonusDetails | undefined => {
  if (typeof code !== "string") return undefined;
  return BONUS_CODES.find((bonusCode) => bonusCode.code === code);
};

export const useBonus = () => {
  const { passportState, walletAddress } = usePassportProvider();
  const getAccessToken = useAccessToken();
  const { mutate } = useGetWalletLatestEarnings(walletAddress ?? undefined);
  const { mutate: refetchGems } = useGetWalletGems(
    walletAddress || null,
    false,
  );
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<BonusError | null>(null);
  const [bonusModalState, setBonusModalState] =
    useState<GenericBonusModalState>(undefined);
  const [bonusDetails, setBonusDetails] = useState<BonusDetails | null>(null);
  const router = useRouter();

  const bonusCode = useMemo(() => {
    const { code } = router.query;
    return findBonusCode(code);
  }, [router.query]);

  useEffect(() => {
    if (bonusCode) {
      localStorage.setItem(bonusCode.storageKey, "true");
      setBonusDetails(bonusCode);
    }
  }, [bonusCode]);

  const bonusCodePresent = useMemo(() => {
    return !!bonusCode;
  }, [bonusCode]);

  const claimBonus = useCallback(async () => {
    if (!bonusCode) return;

    setIsClaiming(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("no_access_token");
      }

      const response = await authorizedPost(
        "/v1/rewards/claim",
        {
          event: bonusCode.event,
        },
        accessToken,
      );

      if (response.claim_status === "already_claimed") {
        setBonusModalState("already_claimed");
        throw new Error("already_claimed");
      }

      setBonusModalState("success");
      trackLegacyEvent({
        screen: "GemBonus",
        action: "Succeeded",
        control: "Screen",
        controlType: "Effect",
        userJourney: "Claim",
        extras: {
          code: bonusCode.code,
        },
      });

      await Promise.all([mutate(), refetchGems()]);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "unknown";
      setError({
        type:
          errorMessage === "no_access_token" ||
          errorMessage === "already_claimed"
            ? errorMessage
            : "unknown",
        message: errorMessage,
      });

      trackLegacyEvent({
        screen: "GemBonus",
        action: "Failed",
        control: "Screen",
        controlType: "Effect",
        userJourney: "Claim",
        extras: {
          error: errorMessage,
        },
      });
    } finally {
      setIsClaiming(false);
      if (bonusCode) {
        localStorage.removeItem(bonusCode.storageKey);
      }
    }
  }, [getAccessToken, mutate, refetchGems, bonusCode]);

  useEffect(() => {
    if (!bonusDetails) return;

    const pendingBonus = localStorage.getItem(bonusDetails.storageKey);
    if (!pendingBonus) return;

    if (
      passportState.ready &&
      !passportState.authenticated &&
      bonusCodePresent
    ) {
      setBonusModalState("login");
    } else if (
      passportState.ready &&
      passportState.authenticated &&
      passportState.zkEvmRegistered &&
      !isClaiming
    ) {
      claimBonus();
    }
  }, [
    passportState.authenticated,
    passportState.ready,
    passportState.zkEvmRegistered,
    claimBonus,
    isClaiming,
    bonusCodePresent,
    bonusDetails,
  ]);

  return {
    bonusModalState,
    setBonusModalState,
    bonusDetails,
    error,
    isClaiming,
  };
};
